import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';
import { ServicemeshNavigationService } from '@helpers/components/navigation/navigation.service';
import { ServicemeshNavigationItem } from '@helpers/components/navigation/navigation.types';
import { ServicemeshVerticalNavigationComponent } from '@helpers/components/navigation/vertical/vertical.component';
import { ServicemeshUtilsService } from '@helpers/services/utils/utils.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'servicemesh-vertical-navigation-basic-item',
  templateUrl: './basic.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    MatTooltipModule,
    NgTemplateOutlet,
    MatIconModule,
  ],
})
export class ServicemeshVerticalNavigationBasicItemComponent implements OnInit, OnDestroy {
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _servicemeshNavigationService = inject(ServicemeshNavigationService);
  private readonly _servicemeshUtilsService = inject(ServicemeshUtilsService);

  @Input() item!: ServicemeshNavigationItem;
  @Input() name!: string;

  isActiveMatchOptions: IsActiveMatchOptions;
  private _servicemeshVerticalNavigationComponent!: ServicemeshVerticalNavigationComponent;
  private readonly _unsubscribeAll: Subject<unknown> = new Subject<unknown>();

  /**
   * Constructor
   */
  constructor() {
    // Set the equivalent of {exact: false} as default for active match options.
    // We are not assigning the item.isActiveMatchOptions directly to the
    // [routerLinkActiveOptions] because if it's "undefined" initially, the router
    // will throw an error and stop working.
    this.isActiveMatchOptions = this._servicemeshUtilsService.subsetMatchOptions;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the "isActiveMatchOptions" either from item's
    // "isActiveMatchOptions" or the equivalent form of
    // item's "exactMatch" option
    if (this.item.isActiveMatchOptions ?? this.item.exactMatch) {
      this.isActiveMatchOptions = this._servicemeshUtilsService.exactMatchOptions;
    } else {
      this.isActiveMatchOptions = this._servicemeshUtilsService.subsetMatchOptions;
    }

    // Get the parent navigation component
    this._servicemeshVerticalNavigationComponent = this._servicemeshNavigationService.getComponent(
      this.name
    );

    // Mark for check
    this._changeDetectorRef.markForCheck();

    // Subscribe to onRefreshed on the navigation component
    this._servicemeshVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
